/**
 * TaskCard - Card de tarefa no Kanban de Tarefas
 * 
 * Layout redesenhado v2.0: grip no topo, título em destaque, descrição truncada,
 * prazo com status, cronômetro compacto, prioridade simplificada e responsável à esquerda.
 * 
 * @version 2.1.0
 * RELEASE: 2026-10-04 v2.4.0 - Reutiliza a regra central do cronômetro de tarefas.
 * RELEASE: 2026-08-22 v2.1.0 - Badge de Período alinhado à direita na mesma linha do status
 * RELEASE: 2026-04-06 v1.0.0 - Criação inicial
 * RELEASE: 2026-04-08 v1.1.0 - Prop canDrag para condicionar handle de arraste
 * RELEASE: 2026-04-12 v2.0.0 - Redesenho completo do layout conforme referência visual
 */

import { useMemo, useState, useEffect } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { format, differenceInDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Grip,
  MessageSquare,
  CalendarClock,
  CheckCircle2,
  ListChecks,
  FileText,
  Timer,
  Paperclip,
  AlertTriangle,
  Clock,
  User,
  Briefcase,
  Scale,
  Gavel,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { getStorageUrl } from '@/lib/storage';
import { cn } from '@/lib/utils';
import { getAvatarColorClasses } from '@/lib/utils/avatar-colors';
import { PriorityFlag, PRIORITY_LABELS } from '@/components/tasks/PriorityFlag';
import type { TaskKanbanItem } from '@/hooks/useTaskKanban';
import { RetentionPeriodBadge } from '@/components/tasks/RetentionPeriodBadge';
import { calculateTaskActiveSeconds, formatTaskTimer } from '@/lib/tasks/taskTimer';

/**
 * Labels de status da tarefa
 */
const STATUS_LABELS: Record<string, string> = {
  pending: 'Pendente',
  in_progress: 'Andamento',
  paused: 'Pausado',
  completed: 'Concluída',
  cancelled: 'Cancelada',
};

/**
 * Estilos de status da tarefa
 */
/** Estilos removidos na v2.1.0 — status agora usa dot+texto inline */

interface TaskCardProps {
  task: TaskKanbanItem;
  /**
   * v1.8.0 — Retenção: datas usadas para calcular a faixa de "Período"
   * (24h, 48h, 72h, 7/10 dias, 1 mês). Fornecidas em lote pela coluna.
   *
   * v2.2.0 — passa a carregar também os identificadores e os dados do módulo
   * (protocolo, nº do processo, advogado, tipo da ação, prazo e valor da causa).
   */
  retentionMeta?: {
    requested_at: string | null;
    completed_at: string | null;
    cancelled_at: string | null;
    protocol_number?: string | null;
    process_number?: string | null;
    lawyer?: string | null;
    action_type?: string | null;
    deadline_date?: string | null;
    case_value?: number | null;
  } | null;
  /** v2.2.0 — módulo corrente ('normal' | 'retention' | 'legal') */
  moduleType?: 'normal' | 'retention' | 'legal';
  onClick?: () => void;
  /** Se o usuário pode arrastar este card (permissão can_change_phase) */
  canDrag?: boolean;
}

export function TaskCard({
  task,
  retentionMeta,
  moduleType = 'normal',
  onClick,
  canDrag = true }: TaskCardProps) {

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: task.id,
    data: { type: 'task', task },
    disabled: !canDrag,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // =========================================================================
  // Cronômetro em tempo real (atualiza a cada segundo se em andamento)
  // =========================================================================
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    // v1.10.0: para o intervalo também quando há completed_at/cancelled_at (congelamento)
    if (
      !task.started_at ||
      task.status === 'completed' ||
      task.status === 'cancelled' ||
      task.is_paused ||
      task.completed_at ||
      task.cancelled_at
    ) {
      return;
    }
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [task.started_at, task.status, task.is_paused, task.completed_at, task.cancelled_at]);

  const activeSeconds = useMemo(
    () => calculateTaskActiveSeconds(
      task.started_at,
      task.is_paused,
      task.total_pause_duration_seconds,
      task.current_pause_started_at,
      task.completed_at,
      task.cancelled_at,
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [task.started_at, task.is_paused, task.total_pause_duration_seconds, task.current_pause_started_at, task.completed_at, task.cancelled_at, now]
  );

  // =========================================================================
  // Prazo: formatar e calcular status de vencimento
  // =========================================================================
  const dueInfo = useMemo(() => {
    if (!task.due_date) return null;
    const dueDate = new Date(task.due_date);
    const formatted = format(dueDate, 'dd/MM/yy', { locale: ptBR });
    const today = new Date();
    const diffDays = differenceInDays(dueDate, today);

    if (task.status === 'completed' || task.status === 'cancelled') {
      return { formatted, label: null, className: 'text-muted-foreground' };
    }
    if (task.is_overdue) {
      const overdueDays = Math.abs(diffDays);
      return {
        formatted,
        label: `Vencido: ${overdueDays}d`,
        className: 'bg-red-500/10 text-red-600 border-red-500/30',
      };
    }
    if (diffDays <= 2) {
      return {
        formatted,
        label: `${diffDays}d restante${diffDays !== 1 ? 's' : ''}`,
        className: 'bg-amber-500/10 text-amber-700 border-amber-500/30',
      };
    }
    return { formatted, label: null, className: 'text-muted-foreground' };
  }, [task.due_date, task.is_overdue, task.status]);

  // Progresso das subtarefas
  const hasSubtasks = task.subtasks_count > 0;
  const subtaskProgress = hasSubtasks ? `${task.subtasks_completed}/${task.subtasks_count}` : null;

  // ===================================================================
  // v2.2.0 — Retenção / Jurídico
  // Nos módulos Retenção e Jurídico o destaque do card passa a ser o nome
  // do cliente/contato vinculado (o título vira linha secundária) e o card
  // exibe o identificador do módulo (protocolo ou nº do processo).
  // ===================================================================
  const isModuleCard = moduleType === 'retention' || moduleType === 'legal';
  const isLegal = moduleType === 'legal';
  const isRetention = moduleType === 'retention';

  /** Nome em destaque: cliente/contato quando existir, senão o título */
  const headline = isModuleCard && task.contact_name ? task.contact_name : task.title;
  /** Título exibido como linha secundária (só quando o destaque é o cliente) */
  const secondaryTitle = isModuleCard && task.contact_name ? task.title : null;

  // v2.2.1 — Jurídico: o nº do processo deixa o header (vira linha simples no corpo);
  // somente Retenção mantém o protocolo ao lado do #número.
  /** Identificador do módulo exibido ao lado do #número (apenas Retenção) */
  const moduleIdentifier = isModuleCard && !isLegal
    ? retentionMeta?.protocol_number || null
    : null;

  /** Prazo processual (Jurídico) — destaque em vermelho quando vencido */
  const legalDeadline = useMemo(() => {
    if (!isLegal || !retentionMeta?.deadline_date) return null;
    const date = new Date(`${retentionMeta.deadline_date}T00:00:00`);
    const isOverdue =
      differenceInDays(date, new Date()) < 0 &&
      task.status !== 'completed' &&
      task.status !== 'cancelled';
    return { formatted: format(date, 'dd/MM/yyyy', { locale: ptBR }), isOverdue };
  }, [isLegal, retentionMeta?.deadline_date, task.status]);

  /** Valor da causa formatado em BRL */
  const caseValueLabel =
    isLegal && retentionMeta?.case_value != null
      ? Number(retentionMeta.case_value).toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        })
      : null;

  return (

    <Card
      ref={setNodeRef}
      style={style}
      className={cn(
        'cursor-pointer hover:shadow-md transition-all duration-200',
        'border border-border/50 bg-card overflow-hidden',
        isDragging && 'opacity-50 shadow-lg rotate-2 scale-105',
        task.is_overdue && 'border-l-2 border-l-red-500'
      )}
      onClick={onClick}
    >
      {/* ================================================================= */}
      {/* Capa: Imagem de preview (estilo Trello) — v1.6.0                  */}
      {/* ================================================================= */}
      {task.cover_image_url && (
        <div className="w-full aspect-video overflow-hidden">
          <img
            src={task.cover_image_url}
            alt={`Capa da tarefa #${task.number}`}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
      )}

      {/* Conteúdo do card com padding */}
      <div className="p-3">
      {/* ================================================================= */}
      {/* Linha 1 - Header: Grip + #Número + Identificador + Destaque       */}
      {/* v2.2.0 — Retenção/Jurídico destacam o cliente e exibem o          */}
      {/* protocolo (Retenção) ou o nº do processo (Jurídico).              */}
      {/* ================================================================= */}
      <div className="flex items-start gap-1.5">
        {/* Handle de arraste inline (topo) */}
        {canDrag && (
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing text-muted-foreground/40 hover:text-muted-foreground mt-0.5 shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            <Grip className="h-3.5 w-3.5" />
          </div>
        )}
        <span className="text-[10px] text-muted-foreground font-mono shrink-0 mt-0.5">
          #{task.number}
        </span>
        <div className="flex-1 min-w-0">
          <span className="block font-semibold text-sm text-foreground line-clamp-2 leading-tight break-words [overflow-wrap:anywhere]">
            {headline}
          </span>
        </div>
      </div>


      {/* ================================================================= */}
      {/* Linha 2 - Descrição truncada em 2 linhas                         */}
      {/* Proteções: min-w-0 + overflow-hidden no wrapper e                */}
      {/* break-words/[overflow-wrap:anywhere] no <p> para impedir que     */}
      {/* strings contínuas (ex: "=====", URLs longas) extrapolem o card. */}
      {/* ================================================================= */}
      {task.description && (
        <div className="flex items-start gap-1 mt-1.5 min-w-0 overflow-hidden">
          <FileText className="h-3 w-3 text-muted-foreground shrink-0 mt-0.5" />
          <p className="text-[11px] text-muted-foreground line-clamp-2 leading-tight flex-1 min-w-0 break-words [overflow-wrap:anywhere]">
            {task.description}
          </p>
        </div>
      )}

      {/* ================================================================= */}
      {/* Linha 2.3 - N. Protocolo (Retenção) — v2.2.2                      */}
      {/* Movido do header para o corpo com label, igual ao modelo Jurídico  */}
      {/* ================================================================= */}
      {isRetention && moduleIdentifier && (
        <div className="flex items-center gap-1 mt-1.5 min-w-0 overflow-hidden">
          <span className="text-[10px] text-muted-foreground font-mono truncate min-w-0">
            N. Protocolo: {moduleIdentifier}
          </span>
        </div>
      )}

      {/* ================================================================= */}
      {/* Linha 2.4 - Dados do Jurídico — v2.2.0                            */}
      {/* Advogado interno, tipo da ação, prazo processual e valor da causa */}
      {/* ================================================================= */}
      {isLegal && (retentionMeta?.process_number || retentionMeta?.lawyer || retentionMeta?.action_type || legalDeadline || caseValueLabel) && (
        <div className="flex flex-col gap-0.5 mt-1.5 text-[10px] text-muted-foreground overflow-hidden min-w-0">
          {/* Nº do processo em linha simples (sem badge) — modelo imagem 2 */}
          {retentionMeta?.process_number && (
            <span className="font-mono truncate min-w-0">
              Nº: {retentionMeta.process_number}
            </span>
          )}
          {/* Advogado + tipo da ação na mesma linha (tipo alinhado à direita) */}
          {(retentionMeta?.lawyer || retentionMeta?.action_type) && (
            <div className="flex items-center gap-1 min-w-0">
              {retentionMeta?.lawyer && (
                <span className="flex items-center gap-0.5 min-w-0">
                  <Scale className="h-3 w-3 shrink-0" />
                  <span className="truncate min-w-0">Advogado: {retentionMeta.lawyer}</span>
                </span>
              )}
              {retentionMeta?.action_type && (
                <span className="flex items-center gap-0.5 min-w-0 shrink-0 ml-auto">
                  <Gavel className="h-3 w-3 shrink-0" />
                  <span className="truncate min-w-0">{retentionMeta.action_type}</span>
                </span>
              )}
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            {legalDeadline && (
              <span
                className={cn(
                  'flex items-center gap-0.5',
                  legalDeadline.isOverdue && 'text-red-600 font-medium',
                )}
              >
                <CalendarClock className="h-3 w-3 shrink-0" />
                Prazo processual: {legalDeadline.formatted}
              </span>
            )}
            {caseValueLabel && (
              <span className="inline-flex items-center rounded-full bg-emerald-500/10 px-1.5 py-px font-mono text-[10px] text-emerald-700">
                {caseValueLabel}
              </span>
            )}
          </div>
        </div>
      )}

      {/* ================================================================= */}
      {/* Linha 2.5 - Cliente e Negócio vinculados — v1.7.0                */}
      {/* v2.2.0 — o cliente não se repete quando já está em destaque       */}
      {/* ================================================================= */}
      {((task.contact_name && !secondaryTitle) || task.deal_title) && (
        <div className="flex flex-col gap-0.5 mt-1.5 text-[10px] text-muted-foreground overflow-hidden min-w-0">
          {task.contact_name && !secondaryTitle && (
            <span className="flex items-center gap-0.5 min-w-0">
              <User className="h-3 w-3 shrink-0" />
              <span className="truncate min-w-0 max-w-[226px]">{task.contact_name}</span>
            </span>
          )}
          {task.deal_title && (
            <span className="flex items-center gap-0.5 min-w-0">
              <Briefcase className="h-3 w-3 shrink-0" />
              <span className="truncate min-w-0 max-w-[226px]">{task.deal_title}</span>
            </span>
          )}
        </div>
      )}


      {/* ================================================================= */}
      {/* Linha 3 - Data de criação + Prazo + status de vencimento         */}
      {/* Apenas formatação visual de campos já vindos da RPC (server-side) */}
      {/* ================================================================= */}
      {(task.created_at || dueInfo) && (
        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
          {/* Data de criação — sempre exibida quando disponível */}
          {task.created_at && (
            <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <Clock className="h-3 w-3 shrink-0" />
              Criado: {format(new Date(task.created_at), 'dd/MM/yy HH:mm', { locale: ptBR })}
            </span>
          )}

          {/* Separador visual quando há ambos */}
          {task.created_at && dueInfo && (
            <span className="text-[10px] text-muted-foreground/60">·</span>
          )}

          {/* Prazo + badge de vencimento (comportamento original preservado) */}
          {dueInfo && (
            <span className="flex items-center gap-1.5">
              <CalendarClock className="h-3 w-3 text-muted-foreground shrink-0" />
              <span className="text-[10px] text-muted-foreground">Prazo: {dueInfo.formatted}</span>
              {dueInfo.label && (
                <span className={cn(
                  'inline-flex items-center gap-px rounded-full px-1.5 py-px text-[9px] font-medium',
                  dueInfo.className
                )}>
                  {task.is_overdue
                    ? <AlertTriangle className="h-3 w-3" />
                    : <Clock className="h-3 w-3" />
                  }
                  {dueInfo.label}
                </span>
              )}
            </span>
          )}
        </div>
      )}

      {/* ================================================================= */}
      {/* Linha 4 - Status + Prioridade + Subtarefas + Período             */}
      {/* v2.1.0 — Badge de Período alinhado à direita na mesma linha do    */}
      {/* status (Retenção e Jurídico).                                    */}
      {/* ================================================================= */}
      <div className="flex items-center justify-between gap-1.5 mt-2">
        {/* Grupo esquerdo: Status + Prioridade + Subtarefas */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {/* Status — texto simples com dot colorido */}
          <span className={cn(
            'inline-flex items-center gap-1 text-[10px] font-medium',
            task.status === 'pending' && 'text-muted-foreground',
            task.status === 'in_progress' && 'text-blue-600',
            task.status === 'paused' && 'text-amber-600',
            task.status === 'completed' && 'text-green-600',
            task.status === 'cancelled' && 'text-red-500',
          )}>
            <span className={cn(
              'w-1.5 h-1.5 rounded-full',
              task.status === 'pending' && 'bg-muted-foreground',
              task.status === 'in_progress' && 'bg-blue-500',
              task.status === 'paused' && 'bg-amber-500',
              task.status === 'completed' && 'bg-green-500',
              task.status === 'cancelled' && 'bg-red-500',
            )} />
            {STATUS_LABELS[task.status] || task.status}
          </span>

          {/* Prioridade: apenas bandeira + texto (sem badge) */}
          {task.priority && task.priority !== 'normal' && (
            <span className="flex items-center gap-0.5 text-[10px] font-medium">
              <PriorityFlag priority={task.priority} size={10} />
              <span className="text-muted-foreground">{PRIORITY_LABELS[task.priority] || task.priority}</span>
            </span>
          )}

          {/* Subtarefas */}
          {hasSubtasks && (
            <span className={cn(
              'flex items-center gap-0.5 text-[10px]',
              task.subtasks_completed === task.subtasks_count ? 'text-green-600' : 'text-muted-foreground'
            )}>
              {task.subtasks_completed === task.subtasks_count
                ? <CheckCircle2 className="h-3 w-3" />
                : <ListChecks className="h-3 w-3" />
              }
              {subtaskProgress}
            </span>
          )}
        </div>

        {/* Badge de Período (Retenção / Jurídico) alinhado à direita */}
        {retentionMeta?.requested_at && (
          <RetentionPeriodBadge
            compact
            requestedAt={retentionMeta.requested_at}
            closedAt={retentionMeta.completed_at || retentionMeta.cancelled_at}
          />
        )}
      </div>

      {/* ================================================================= */}
      {/* Linha 5 - Rodapé: Responsável (esquerda) + Timer + Comentários   */}
      {/* ================================================================= */}
      <div className="flex items-center justify-between gap-2 mt-2">
        {/* Lado esquerdo: Avatar + nome do responsável */}
        <div className="flex items-center gap-1.5 min-w-0 flex-1">
          {task.assigned_to && (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1 min-w-0">
                  <Avatar className="h-5 w-5 shrink-0">
                    <AvatarImage
                      src={getStorageUrl(task.assigned_to_avatar)}
                      alt={task.assigned_to_name || 'Responsável'}
                    />
                    <AvatarFallback className={cn(
                      "text-[8px]",
                      getAvatarColorClasses(task.assigned_to_name)
                    )}>
                      {(task.assigned_to_name || 'AG').slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-[10px] text-muted-foreground truncate max-w-[80px]">
                    {task.assigned_to_name || 'Responsável'}
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">
                {task.assigned_to_name || 'Responsável'}
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* Lado direito: Cronômetro + Comentários */}
        <div className="flex items-center gap-2 shrink-0">
          {/* Cronômetro compacto — exibe sempre que houver started_at (v1.3.0).
              Tarefas sem prazo continuam contando normalmente, pois o prazo
              não é pré-requisito para a contagem de tempo de trabalho. */}
          {task.started_at && (
            <span className={cn(
              'flex items-center gap-0.5 text-[10px] font-mono',
              task.is_paused ? 'text-amber-600' : 'text-muted-foreground'
            )}>
              <Timer className="h-3 w-3" />
              {formatTaskTimer(activeSeconds)}
            </span>
          )}

          {/* Anexos — v1.6.0 */}
          {task.attachments_count > 0 && (
            <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
              <Paperclip className="h-3 w-3" />
              {task.attachments_count}
            </span>
          )}

          {/* Comentários */}
          {task.comments_count > 0 && (
            <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
              <MessageSquare className="h-3 w-3" />
              {task.comments_count}
            </span>
          )}
        </div>
      </div>
      </div>
    </Card>
  );
}
